import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
// @ts-ignore
import {Key, Plus, Copy, CheckCircle, Trash, WarningCircle, X} from "@phosphor-icons/react";
import {getAxiosBaseUrl} from "../../config";
import {auth} from "../../firebase";

// --------------------------------------------------------------------------
// Helpers

async function authHeaders() {
	// Firebase ID token authenticates the API-key management endpoints
	// themselves (they don't accept API keys — that would be circular).
	const user = auth && auth.currentUser;
	if (!user) throw new Error("Not signed in");
	const token = await user.getIdToken();
	return {Authorization: `Bearer ${token}`};
}

function fmtDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleDateString(undefined, {year: "numeric", month: "short", day: "numeric"});
	} catch (e) {
		return "—";
	}
}

function maskedPrefix(prefix) {
	// Backend returns the first chunk of the key (e.g. "nm_a3f7"). Display
	// it with an ellipsis so it's clear the rest is hidden.
	if (!prefix) return "—";
	return `${prefix}…`;
}

// --------------------------------------------------------------------------

export default function ApiKeys() {
	const [keys, setKeys] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [newName, setNewName] = useState("");
	const [creating, setCreating] = useState(false);
	const [rawKey, setRawKey] = useState(null); // shown ONCE after mint
	const [copied, setCopied] = useState(false);
	const [revokingId, setRevokingId] = useState(null);

	const nameInputRef = useRef(null);

	// ----- Load existing keys -----
	const loadKeys = async () => {
		setLoading(true);
		try {
			const headers = await authHeaders();
			const res = await axios.get(`${getAxiosBaseUrl()}/api/v1/auth/keys`, {headers});
			// Backend returns a raw array; tolerate both shapes for forward-compat.
			const data = res && res.data;
			const list = Array.isArray(data) ? data : (Array.isArray(data && data.keys) ? data.keys : []);
			setKeys(list);
		} catch (err) {
			console.log("list api keys error", err);
			toast.error("Could not load API keys");
			setKeys([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadKeys();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ----- Mint a new key -----
	const openMintModal = () => {
		setNewName("");
		setRawKey(null);
		setCopied(false);
		setShowModal(true);
		// Focus the name input on next tick.
		setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 30);
	};

	const closeMintModal = () => {
		setShowModal(false);
		// IMPORTANT: drop the raw key from state when modal closes — it must
		// never linger in component memory after the user dismisses the dialog.
		setRawKey(null);
		setNewName("");
		setCopied(false);
	};

	const mintKey = async () => {
		const name = (newName || "").trim();
		if (!name) {
			toast.error("Give the key a name first");
			return;
		}
		setCreating(true);
		try {
			const headers = await authHeaders();
			const res = await axios.post(
				`${getAxiosBaseUrl()}/api/v1/auth/keys`,
				{name},
				{headers}
			);
			const data = res && res.data ? res.data : {};
			if (!data.key) throw new Error("Server did not return a key");
			setRawKey(data.key);
			// Refresh the list so the new key (without the raw secret) appears.
			loadKeys();
		} catch (err) {
			console.log("mint api key error", err);
			toast.error("Could not create API key");
		} finally {
			setCreating(false);
		}
	};

	const copyRawKey = async () => {
		if (!rawKey) return;
		try {
			await navigator.clipboard.writeText(rawKey);
			setCopied(true);
			setTimeout(() => setCopied(false), 1800);
		} catch (e) {
			toast.error("Copy failed — select the text manually");
		}
	};

	// ----- Revoke -----
	const revokeKey = async (id) => {
		const ok = window.confirm("Revoke this key? Any agent or script using it will start failing immediately. This can't be undone.");
		if (!ok) return;
		setRevokingId(id);
		try {
			const headers = await authHeaders();
			await axios.delete(`${getAxiosBaseUrl()}/api/v1/auth/keys/${id}`, {headers});
			toast.success("Key revoked");
			loadKeys();
		} catch (err) {
			console.log("revoke api key error", err);
			toast.error("Could not revoke key");
		} finally {
			setRevokingId(null);
		}
	};

	// ----- Derived -----
	const {active, revoked} = useMemo(() => {
		const a = [], r = [];
		for (const k of keys) {
			if (k.revoked_at || k.revoked) r.push(k);
			else a.push(k);
		}
		return {active: a, revoked: r};
	}, [keys]);

	// ----- Render -----
	return (
		<div className="max-w-4xl mx-auto px-6 py-10">
			{/* Header */}
			<div className="flex items-start justify-between mb-8">
				<div>
					<h1 className="text-3xl font-semibold text-slate-900 mb-1">API keys</h1>
					<p className="text-slate-500 text-sm">
						Use these to drive Neugence Marketing from scripts, agents, or the MCP server.
					</p>
				</div>
				<button
					type="button"
					onClick={openMintModal}
					className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2.5 font-semibold shadow-sm transition-colors"
				>
					<Plus size={18} weight="bold"/>
					Generate new key
				</button>
			</div>

			{/* Loading */}
			{loading && (
				<div className="text-center py-16 text-slate-400 text-sm">Loading…</div>
			)}

			{/* Empty state */}
			{!loading && keys.length === 0 && (
				<div className="rounded-2xl shadow-sm border border-slate-100 bg-white px-8 py-16 text-center">
					<div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-500 mb-4">
						<Key size={28} weight="duotone"/>
					</div>
					<h3 className="text-lg font-semibold text-slate-800 mb-1">No API keys yet</h3>
					<p className="text-slate-500 text-sm mb-6">
						Generate a key to connect Claude Code, the MCP server, or your own scripts.
					</p>
					<button
						type="button"
						onClick={openMintModal}
						className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2.5 font-semibold shadow-sm"
					>
						<Plus size={18} weight="bold"/>
						Generate your first key
					</button>
				</div>
			)}

			{/* Active keys */}
			{!loading && active.length > 0 && (
				<section className="mb-8">
					<h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Active</h2>
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white overflow-hidden">
						{active.map((k, idx) => (
							<KeyRow
								key={k.id || idx}
								apiKey={k}
								onRevoke={revokeKey}
								busy={revokingId === k.id}
							/>
						))}
					</div>
				</section>
			)}

			{/* Revoked keys */}
			{!loading && revoked.length > 0 && (
				<section className="mb-8">
					<h2 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Revoked</h2>
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white overflow-hidden opacity-60">
						{revoked.map((k, idx) => (
							<KeyRow
								key={k.id || idx}
								apiKey={k}
								onRevoke={null}
								busy={false}
								revoked
							/>
						))}
					</div>
				</section>
			)}

			{/* MCP setup card */}
			<section className="mt-10">
				<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6">
					<h3 className="text-lg font-semibold text-slate-900 mb-1">Use these keys with an MCP client</h3>
					<p className="text-slate-500 text-sm mb-4">
						To use these keys with Claude Code or another MCP client, add to your settings:
					</p>
					<pre className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-sm text-slate-800 overflow-x-auto leading-relaxed">
{`{
  "mcpServers": {
    "neugence": {
      "url": "https://app.neugence.com/mcp/sse",
      "headers": { "Authorization": "Bearer YOUR_KEY" }
    }
  }
}`}
					</pre>
				</div>
			</section>

			{/* Mint modal */}
			{showModal && (
				<MintKeyModal
					name={newName}
					onNameChange={setNewName}
					onMint={mintKey}
					onClose={closeMintModal}
					creating={creating}
					rawKey={rawKey}
					copied={copied}
					onCopy={copyRawKey}
					nameInputRef={nameInputRef}
				/>
			)}
		</div>
	);
}

// --------------------------------------------------------------------------

function KeyRow({apiKey, onRevoke, busy, revoked}) {
	return (
		<div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-100 last:border-b-0">
			<div className="flex items-center gap-4 min-w-0">
				<div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
					<Key size={20} weight="duotone"/>
				</div>
				<div className="min-w-0">
					<div className="font-medium text-slate-900 truncate">{apiKey.name || "Untitled key"}</div>
					<div className="text-xs text-slate-500 font-mono truncate">{maskedPrefix(apiKey.prefix)}</div>
				</div>
			</div>
			<div className="hidden sm:flex flex-col items-end text-right text-xs text-slate-500 shrink-0">
				<span>Created {fmtDate(apiKey.created_at)}</span>
				<span>Last used {fmtDate(apiKey.last_used_at)}</span>
			</div>
			<div className="shrink-0">
				{revoked ? (
					<span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-slate-100 text-slate-600">
						Revoked
					</span>
				) : (
					<button
						type="button"
						aria-label="Revoke key"
						onClick={() => onRevoke && onRevoke(apiKey.id)}
						disabled={busy}
						className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
					>
						<Trash size={14} weight="bold"/>
						{busy ? "Revoking…" : "Revoke"}
					</button>
				)}
			</div>
		</div>
	);
}

// --------------------------------------------------------------------------

function MintKeyModal({name, onNameChange, onMint, onClose, creating, rawKey, copied, onCopy, nameInputRef}) {
	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="mint-key-title"
			className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
		>
			<div className="w-full max-w-lg rounded-2xl shadow-xl border border-slate-100 bg-white">
				<div className="flex items-start justify-between px-6 pt-5 pb-3">
					<h2 id="mint-key-title" className="text-lg font-semibold text-slate-900">
						{rawKey ? "Your new API key" : "Generate API key"}
					</h2>
					<button
						type="button"
						aria-label="Close"
						onClick={onClose}
						className="text-slate-400 hover:text-slate-600 -mt-1 -mr-2 p-2"
					>
						<X size={18} weight="bold"/>
					</button>
				</div>

				<div className="px-6 pb-6">
					{!rawKey ? (
						<>
							<label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="mint-key-name">
								Key name
							</label>
							<input
								id="mint-key-name"
								ref={nameInputRef}
								type="text"
								value={name}
								onChange={(e) => onNameChange(e.target.value)}
								placeholder="e.g. Claude Code on laptop"
								className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition-colors"
								onKeyDown={(e) => {
									if (e.key === "Enter" && !creating) {
										e.preventDefault();
										onMint();
									}
								}}
							/>
							<p className="text-xs text-slate-500 mt-2">
								A label to help you find this key later. Visible only to you.
							</p>
							<div className="flex justify-end gap-2 mt-6">
								<button
									type="button"
									onClick={onClose}
									className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
								>
									Cancel
								</button>
								<button
									type="button"
									onClick={onMint}
									disabled={creating || !(name || "").trim()}
									className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2 font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
								>
									{creating ? "Generating…" : "Generate"}
								</button>
							</div>
						</>
					) : (
						<>
							<div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 flex items-start gap-2">
								<WarningCircle size={18} weight="fill" className="text-amber-500 mt-0.5 shrink-0"/>
								<div className="text-sm text-amber-900">
									<strong className="font-semibold">Save this — you won't see it again.</strong>{" "}
									Store it in a password manager or your shell config now.
								</div>
							</div>
							<div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 break-all select-all">
								{rawKey}
							</div>
							<div className="flex justify-end gap-2 mt-4">
								<button
									type="button"
									onClick={onCopy}
									className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50"
								>
									{copied ? <CheckCircle size={16} weight="bold"/> : <Copy size={16} weight="bold"/>}
									{copied ? "Copied" : "Copy"}
								</button>
								<button
									type="button"
									onClick={onClose}
									className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-5 py-2 font-semibold shadow-sm"
								>
									Done
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
